import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
//
import PlayerDetailPage from './pages/PlayerDetailPage';
import NewPlayerPage from './pages/NewPlayerPage';
import OptaImportTournamentsPage from './pages/OptaImportTournamentsPage';
import TournamentMatchdaysPage from './pages/TournamentMatchdaysPage';
import MatchdayDetailPage from './pages/MatchdayDetailPage';
import MatchdayBuilderPage from './pages/MatchdayBuilderPage';
import TournamentGameweeksPage from './pages/TournamentGameweeksPage';
import GameweekDetailPage from './pages/GameweekDetailPage';
import GameweekBuilderPage from './pages/GameweekBuilderPage';
import TournamentListPage from './pages/TournamentListPage';
import TournamentDetailPage from './pages/TournamentDetailPage';
import BoosterConfigurationPage from './pages/BoosterConfigurationPage';
import BoosterUsagePage from './pages/BoosterUsagePage';
import LoginPage from './pages/LoginPage';
import Page404 from './pages/Page404';
import HomePage from './pages/HomePage';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import UserTabShortcutPage from './pages/UserTabShortcutPage';
import UserSegmentationPage from './pages/UserSegmentationPage';
import UserAdminActionPage from './pages/UserAdminActionPage';
import PlayersPage from './pages/PlayersPage';
import DashboardAppPage from './pages/DashboardAppPage';
import EcffFixturesPage from './pages/EcffFixturesPage';
import EcffSquadsPage from './pages/EcffSquadsPage';
import EcffLeaguesPage from './pages/EcffLeaguesPage';
import EcffWinnersPage from './pages/EcffWinnersPage';
import WeeklyGameweekPrizesPage from './pages/WeeklyGameweekPrizesPage';
import OptaImportPage from './pages/OptaImportPage';
import OtherLeagueImportPage from './pages/OtherLeagueImportPage';
import SyncPlayersPage from './pages/SyncPlayersPage';
import OptaSquadsPage from './pages/OptaSquadsPage';
import UserActivityPage from './pages/UserActivityPage';
import AdminsPage from './pages/AdminsPage';
import MatchSimulatorPage from './pages/MatchSimulatorPage';
import ReScoringPage from './pages/ReScoringPage';
import ScoringRulesListPage from './pages/ScoringRulesListPage';
import ScoringRulesConfigPage from './pages/ScoringRulesConfigPage';
import AreasCountriesPage from './pages/AreasCountriesPage';
import AreasTabPage from './pages/AreasTabPage';
import CountriesTabPage from './pages/CountriesTabPage';
import AppSettingsPage from './pages/AppSettingsPage';
import NotificationRecordsPage from './pages/NotificationRecordsPage';
import CurrencyRecordsPage from './pages/CurrencyRecordsPage';
import AwardRecordsPage from './pages/AwardRecordsPage';

// ----------------------------------------------------------------------

export default function Router() {
  const routes = useRoutes([
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to="/dashboard/home" />, index: true },
        { path: 'players/new', element: <NewPlayerPage /> },
        { path: 'players/:playerId', element: <PlayerDetailPage /> },
        { path: 'tournaments', element: <TournamentListPage /> },
        { path: 'tournaments/:id', element: <TournamentDetailPage /> },
        { path: 'tournaments/:id/matchdays', element: <TournamentMatchdaysPage /> },
        { path: 'tournaments/:id/matchdays/:matchdayId', element: <MatchdayDetailPage /> },
        { path: 'tournaments/:id/matchdays/:matchdayId/edit', element: <MatchdayDetailPage /> },
        { path: 'tournaments/:id/matchday-builder', element: <MatchdayBuilderPage /> },
        { path: 'tournaments/:id/gameweeks', element: <TournamentGameweeksPage /> },
        { path: 'tournaments/:id/gameweeks/:gameweekId', element: <GameweekDetailPage /> },
        { path: 'tournaments/:id/gameweeks/:gameweekId/edit', element: <GameweekDetailPage /> },
        { path: 'tournaments/:id/gameweek-builder', element: <GameweekBuilderPage /> },
        { path: 'boosters', element: <Navigate to="/dashboard/boosters/configuration" /> },
        { path: 'boosters/configuration', element: <BoosterConfigurationPage /> },
        { path: 'boosters/usage', element: <BoosterUsagePage /> },
        { path: 'ecff/opta-import/tournaments', element: <OptaImportTournamentsPage /> },
        { path: 'home', element: <HomePage /> },
        { path: 'users', element: <UsersPage /> },
        { path: 'users/list', element: <UsersPage /> },
        {
          path: 'users/detail-team',
          element: (
            <UserTabShortcutPage
              title="User Detail - Team View"
              description="Search for a user and jump straight to their squad tab."
              actionLabel="Open squad"
              tab="squad"
            />
          ),
        },
        {
          path: 'users/detail-booster',
          element: (
            <UserTabShortcutPage
              title="User Detail - Booster Usage"
              description="Search for a user and jump straight to their booster usage tab."
              actionLabel="Open boosters"
              tab="boosters"
            />
          ),
        },
        {
          path: 'users/detail-transfer',
          element: (
            <UserTabShortcutPage
              title="User Detail - Transfer History"
              description="Search for a user and jump straight to their transfer history tab."
              actionLabel="Open transfers"
              tab="transfers"
            />
          ),
        },
        { path: 'users/segmentation', element: <UserSegmentationPage /> },
        {
          path: 'users/suspension',
          element: (
            <UserAdminActionPage
              title="Account Suspension / Banning"
              description="Record an account suspension or banning request for ops review."
              action="suspension_request"
              actionLabel="Record suspension request"
              successLabel="Suspension request recorded."
            />
          ),
        },
        {
          path: 'users/gdpr',
          element: (
            <UserAdminActionPage
              title="GDPR / Data Deletion Tools"
              description="Record a GDPR deletion request against a selected user, or execute the erasure."
              action="gdpr_deletion_request"
              actionLabel="Record GDPR request"
              successLabel="GDPR request recorded."
            />
          ),
        },
        {
          path: 'users/communication',
          element: (
            <UserAdminActionPage
              title="User Communication Tools"
              description="Queue a user communication request for the selected user."
              action="communication_request"
              actionLabel="Queue communication"
              successLabel="Communication request queued."
            />
          ),
        },
        { path: 'users/:userId', element: <UserDetailPage /> },
        { path: 'user-activity', element: <UserActivityPage view="overview" /> },
        { path: 'user-activity/total', element: <UserActivityPage view="total" /> },
        {
          path: 'user-activity/daily-registration',
          element: <UserActivityPage view="registrations" />,
        },
        { path: 'user-activity/engagement', element: <UserActivityPage view="engagement" /> },
        { path: 'user-activity/audit', element: <UserActivityPage view="audit" /> },
        { path: 'admins', element: <AdminsPage /> },
        { path: 'players', element: <PlayersPage /> },
        { path: 'scoring-rules', element: <ScoringRulesListPage /> },
        { path: 'scoring-rules/:tournamentCalendarId', element: <ScoringRulesConfigPage /> },
        { path: 'tools/match-simulator', element: <MatchSimulatorPage /> },
        { path: 'tools/re-scoring', element: <ReScoringPage /> },
        { path: 'app-settings', element: <AppSettingsPage /> },
        { path: 'delivery-records/notifications', element: <NotificationRecordsPage /> },
        { path: 'delivery-records/gamification-awards', element: <AwardRecordsPage /> },
        { path: 'delivery-records/currencies', element: <CurrencyRecordsPage /> },
        { path: 'ecff/fixtures', element: <EcffFixturesPage /> },
        { path: 'ecff/squads', element: <EcffSquadsPage /> },
        { path: 'ecff/opta-squads', element: <OptaSquadsPage /> },
        { path: 'ecff/leagues', element: <EcffLeaguesPage /> },
        { path: 'ecff/winners', element: <EcffWinnersPage /> },
        { path: 'ecff/weekly-prizes', element: <WeeklyGameweekPrizesPage /> },
        { path: 'ecff/opta-import', element: <OptaImportPage /> },
        { path: 'ecff/opta-import/sync-players', element: <SyncPlayersPage /> },
        { path: 'ecff/other-league-import', element: <OtherLeagueImportPage /> },
        { path: 'app', element: <DashboardAppPage /> },
        {
          path: 'areas-countries',
          element: <AreasCountriesPage />,
          children: [
            { index: true, element: <Navigate to="areas" replace /> },
            { path: 'areas', element: <AreasTabPage /> },
            { path: 'countries', element: <CountriesTabPage /> },
            { path: '*', element: <Navigate to="areas" replace /> },
          ],
        },
      ],
    },
    {
      path: 'login',
      element: <LoginPage />,
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/dashboard/home" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
